import { Controller, useWatch } from "react-hook-form";
import { useLocalization } from "@fluent/react";
import {
  POSTAL_CODE_NOT_APPLICABLE_COUNTRIES,
  SWIFT_DESTINATION_COUNTRIES,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { InternationalAddressAutocompleteInput } from "cerulean";
import { ContentCard, TextInput } from "@narmi/design_system";

import CountrySelector from "../../../../../../components/form/CountrySelector";
import AddressRow from "../AddressRow";

import styles from "./InternationalWireForm.module.scss";

import type { SwiftDestinationFormType } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import type { SwiftAddressField } from "byzantine/src/dbbl/businessLogic/modules/recipientDrawer";
import type { Control, FieldValues } from "react-hook-form";

// the API's errors for one address, by field
export type AddressErrors = Record<string, string | undefined>;

interface AddressFieldsProps {
  control: Control<SwiftDestinationFormType>;
  fieldName: SwiftAddressField;
  title?: string;
  // the country's own names for fields, e.g. region_code is "Province"
  renameFields: Record<string, string> | null;
  errors?: AddressErrors;
  countryError?: string;
  onCountryChange: (countryCode: string) => void;
  onAutocomplete: (update: Partial<API.Address>) => void;
}

const AddressFields = ({
  control,
  fieldName,
  title,
  renameFields,
  errors,
  countryError,
  onCountryChange,
  onAutocomplete,
}: AddressFieldsProps) => {
  const { l10n } = useLocalization();
  const countryCode = useWatch({ control, name: `${fieldName}.country_code` });

  return (
    <ContentCard kind="plain" paddingSize="none">
      {title ? (
        <p className={`fontColor--secondary ${styles.sectionSubHeader}`}>
          {title}
        </p>
      ) : null}
      <div className="margin--bottom--l">
        <Controller
          name={`${fieldName}.country_code`}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CountrySelector
              label={l10n.getString("recipient-drawer-country-label")}
              countries={SWIFT_DESTINATION_COUNTRIES}
              onChange={(code) => {
                onChange(code);
                onCountryChange(code);
              }}
              value={value ?? ""}
              error={error?.message || errors?.country_code || countryError}
            />
          )}
        />
      </div>
      <AddressRow
        gapSize="m"
        className="margin--bottom--l"
        wide={
          <InternationalAddressAutocompleteInput
            control={control as unknown as Control<FieldValues>}
            name={`${fieldName}.street_address`}
            label={l10n.getString("label-street-address")}
            onUpdate={onAutocomplete}
            isInternational
            error={errors?.street_address}
          />
        }
        narrow={
          <Controller
            name={`${fieldName}.street_address_2`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                value={field.value || ""}
                field={`${fieldName}.street_address_2`}
                label={l10n.getString("label-address-2")}
                error={error?.message || errors?.street_address_2}
              />
            )}
          />
        }
      />
      <div className="margin--bottom--l">
        <Controller
          name={`${fieldName}.city`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              value={field.value || ""}
              field={`${fieldName}.city`}
              label={l10n.getString("label-city")}
              error={error?.message || errors?.non_field_errors || errors?.city}
            />
          )}
        />
      </div>
      <AddressRow
        gapSize="m"
        wide={
          <Controller
            name={`${fieldName}.region_code`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                value={field.value || ""}
                field={`${fieldName}.region_code`}
                label={
                  renameFields?.region_code ||
                  l10n.getString("label-region-code")
                }
                error={error?.message || errors?.region_code}
              />
            )}
          />
        }
        narrow={
          !POSTAL_CODE_NOT_APPLICABLE_COUNTRIES.has(countryCode || "") ? (
            <Controller
              name={`${fieldName}.postal_code`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  value={field.value || ""}
                  field={`${fieldName}.postal_code`}
                  label={l10n.getString("label-postal-code")}
                  error={error?.message || errors?.postal_code}
                />
              )}
            />
          ) : null
        }
      />
    </ContentCard>
  );
};

export default AddressFields;
